/**
 * Topic hubs — SEO landing pages aggregating articles by tag/category to win
 * editorial head-keyword traffic identified in the keyword-gap analysis vs.
 * oilprice.com and rigzone.com.
 *
 * Each hub renders a unique H1 with the head keyword verbatim, an intro
 * paragraph that establishes topical relevance, and a continuously-updated
 * grid of the latest matching articles from the database.
 */

export interface TopicHub {
  slug: string;
  /** The exact head keyword — used verbatim in <h1>. */
  headKeyword: string;
  /** SEO <title> (excludes brand suffix; the page appends it). */
  title: string;
  /** Meta description, ~150 chars. */
  description: string;
  /** One-paragraph intro shown above the article grid. */
  intro: string;
  /** Match if any of these tags appear on the article (case-insensitive). */
  tagsAny: string[];
  /** Match if the article's category equals any of these (case-insensitive). */
  categoriesAny?: string[];
  /** Slugs of related hubs to cross-link at the bottom for internal linking. */
  relatedSlugs: string[];
}

export const TOPIC_HUBS: TopicHub[] = [
  {
    slug: "oil-and-gas-news-today",
    headKeyword: "Oil and Gas News Today",
    title: "Oil and Gas News Today — Latest Crude, OPEC & LNG Coverage",
    description:
      "Oil and gas news today: latest crude oil prices, OPEC+ decisions, LNG flows and energy security developments from Global Energy Insider's editorial desk.",
    intro:
      "The latest oil and gas news today, updated continuously by our energy desk. Coverage spans crude oil price moves, OPEC+ policy, LNG cargo flows, refinery and pipeline developments, and the geopolitical catalysts driving global energy markets.",
    tagsAny: [
      "Oil Prices",
      "Crude Oil",
      "OPEC+",
      "Brent Crude",
      "WTI",
      "Natural Gas",
      "LNG",
      "Oil Production",
    ],
    categoriesAny: ["Oil, Gas & Commodities", "Energy & Commodities"],
    relatedSlugs: ["crude-oil-news", "opec-news", "natural-gas-and-lng-news", "energy-security"],
  },
  {
    slug: "crude-oil-news",
    headKeyword: "Crude Oil News",
    title: "Crude Oil News — WTI, Brent, Prices & Production Updates",
    description:
      "Crude oil news covering WTI and Brent prices, OPEC+ supply moves, US shale output, and the catalysts moving the global crude oil market today.",
    intro:
      "Independent crude oil news from Global Energy Insider. Track WTI and Brent benchmarks, US shale production, OPEC+ supply policy, refinery margins, and the geopolitical events shaping the price of crude oil this week.",
    tagsAny: ["Crude Oil", "WTI", "Brent Crude", "Oil Prices", "Oil Production"],
    categoriesAny: ["Oil, Gas & Commodities", "Energy & Commodities"],
    relatedSlugs: ["opec-news", "oil-and-gas-news-today", "energy-security"],
  },
  {
    slug: "opec-news",
    headKeyword: "OPEC News",
    title: "OPEC News — OPEC+ Decisions, Quotas & Market Impact",
    description:
      "OPEC news and analysis: OPEC+ production quotas, ministerial meetings, compliance data, and how each decision shapes global crude oil prices.",
    intro:
      "OPEC news from Global Energy Insider — focused on named catalysts, not generic institutional summaries. We track OPEC+ ministerial meetings, voluntary cuts, quota compliance, and the price reaction across Brent and WTI benchmarks.",
    tagsAny: ["OPEC+", "Oil Production", "Oil Prices"],
    relatedSlugs: ["crude-oil-news", "oil-and-gas-news-today", "energy-security"],
  },
  {
    slug: "natural-gas-and-lng-news",
    headKeyword: "Natural Gas and LNG News",
    title: "Natural Gas & LNG News — Prices, Cargoes & Pipeline Flows",
    description:
      "Natural gas and LNG news: TTF and Henry Hub prices, US export cargoes, European storage levels, and pipeline flow disruptions across global gas markets.",
    intro:
      "The latest natural gas and LNG news from Global Energy Insider. Coverage includes Henry Hub and TTF benchmarks, US LNG export cargo flows, European storage and demand, pipeline disruptions, and long-term offtake agreements reshaping global gas trade.",
    tagsAny: ["LNG", "Natural Gas", "natural gas"],
    categoriesAny: ["Oil, Gas & Commodities", "Energy & Commodities"],
    relatedSlugs: ["oil-and-gas-news-today", "energy-security", "energy-transition-news"],
  },
  {
    slug: "energy-security",
    headKeyword: "Energy Security",
    title: "Energy Security News — Geopolitics, Chokepoints & Supply Risk",
    description:
      "Energy security news covering Strait of Hormuz, Red Sea shipping, Russia sanctions, Middle East conflict, and the geopolitical risks to global energy supply.",
    intro:
      "Energy security coverage from Global Energy Insider's geopolitics desk. We track chokepoint risk at the Strait of Hormuz and Red Sea, Russia and Iran sanctions enforcement, Middle East conflict spillover, and the policy responses shaping supply resilience.",
    tagsAny: [
      "Energy Security",
      "Geopolitics",
      "Strait of Hormuz",
      "Middle East",
      "Iran",
      "Russia",
      "Red Sea",
      "Middle East Conflict",
    ],
    categoriesAny: ["Geopolitics & Security", "Policy & Regulations"],
    relatedSlugs: ["shipping-and-supply-chain-news", "crude-oil-news", "opec-news"],
  },
  {
    slug: "shipping-and-supply-chain-news",
    headKeyword: "Shipping and Supply Chain News",
    title: "Shipping & Supply Chain News — Tankers, Routes & Freight Rates",
    description:
      "Shipping and supply chain news: tanker rates, Red Sea diversions, Suez and Panama Canal flows, port congestion, and global freight market disruptions.",
    intro:
      "Shipping and supply chain news from Global Energy Insider's maritime desk. Coverage includes crude and product tanker rates, Red Sea routing decisions, Suez and Panama Canal throughput, port congestion, and the supply-chain knock-ons across energy and commodity flows.",
    tagsAny: ["Shipping", "Supply Chain", "Logistics", "Red Sea", "supply chain"],
    categoriesAny: ["Maritime & Shipping", "Shipping"],
    relatedSlugs: ["energy-security", "oil-and-gas-news-today"],
  },
  {
    slug: "energy-transition-news",
    headKeyword: "Energy Transition News",
    title: "Energy Transition News — Renewables, Policy & Investment",
    description:
      "Energy transition news covering renewables build-out, climate policy, clean-tech investment, and how the shift away from fossil fuels is reshaping markets.",
    intro:
      "Energy transition news from Global Energy Insider. We cover renewables capacity additions, climate and decarbonisation policy, clean-tech project finance, and the structural shifts reshaping how the world produces and consumes energy.",
    tagsAny: ["Energy Transition", "energy transition", "Renewables"],
    categoriesAny: ["Renewables & Energy Transition"],
    relatedSlugs: ["oil-and-gas-news-today", "natural-gas-and-lng-news"],
  },
];

export function getTopicHub(slug: string): TopicHub | undefined {
  return TOPIC_HUBS.find((h) => h.slug === slug);
}

/** All topic-hub slugs — used for sitemap generation and footer linking. */
export const TOPIC_HUB_SLUGS = TOPIC_HUBS.map((h) => h.slug);

/**
 * Score how relevant a topic hub is to an article by counting tag overlap +
 * category match. Returns hubs sorted by score (desc), filtered to score > 0.
 */
export function getRelevantTopicHubs(
  article: { tags: string[]; category: string },
  limit = 3,
): TopicHub[] {
  const articleTags = new Set(article.tags.map((t) => t.toLowerCase()));
  const articleCat = article.category.toLowerCase();

  const scored = TOPIC_HUBS.map((hub) => {
    const tagHits = hub.tagsAny.filter((t) => articleTags.has(t.toLowerCase())).length;
    const catHit = hub.categoriesAny?.some((c) => c.toLowerCase() === articleCat) ? 1 : 0;
    // Tag overlap weighted higher than category match
    const score = tagHits * 2 + catHit;
    return { hub, score };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored.map((s) => s.hub);
}
